import { atom } from "jotai";
import useHttp from "../hooks/useHttp";
import { createFacetMappings } from "../utils/seoUrlMapping";

// Atoms for loading state
export const facetMetaLoadingStatusAtom = atom("idle");

// Atom for storing facet_meta data
export const facetMetaDataAtom = atom(null);

// Atom for storing errors
export const facetMetaErrorAtom = atom(null);

// Atom for data caching
export const facetMetaCacheAtom = atom({});

// Atom for storing last query
export const lastFacetMetaQueryAtom = atom(null);

// Atom for storing ID ↔ SEO slug mappings
export const facetMappingsAtom = atom(null);

// Atom for fetching facet_meta data
export const fetchFacetMetaAtom = atom(null, async (get, set, query = "") => {
  const { getFacetMeta } = useHttp();
  
  // Check cache
  const cache = get(facetMetaCacheAtom);
  const cacheKey = query || "default";
  
  if (cache[cacheKey]) {
    set(facetMetaDataAtom, cache[cacheKey]);
    set(facetMetaLoadingStatusAtom, "idle");
    return;
  }

  set(facetMetaLoadingStatusAtom, "loading");
  set(facetMetaErrorAtom, null);
  set(lastFacetMetaQueryAtom, query);

  try {
    const response = await getFacetMeta();
    
    // Create mappings from received data
    let mappings = null;
    if (response?.results?.facets) {
      mappings = createFacetMappings(response.results.facets);
    }
    
    // Update cache
    set(facetMetaCacheAtom, {
      ...cache,
      [cacheKey]: response
    });
    
    set(facetMetaDataAtom, response);
    set(facetMappingsAtom, mappings);
    set(facetMetaLoadingStatusAtom, "idle");
  } catch (error) {
    set(facetMetaErrorAtom, error);
    set(facetMetaLoadingStatusAtom, "error");
    console.error("Error fetching facet meta:", error);
  }
});

// Atom for clearing cache
export const clearFacetMetaCacheAtom = atom(null, (get, set) => {
  set(facetMetaCacheAtom, {});
  set(facetMetaDataAtom, null);
  set(facetMetaErrorAtom, null);
  set(lastFacetMetaQueryAtom, null);
  set(facetMappingsAtom, null);
});

// Atom for getting specific facet_meta by key
export const getFacetMetaByKeyAtom = atom((get) => {
  const facetMetaData = get(facetMetaDataAtom);
  if (!facetMetaData) return null;
  
  return (key) => {
    if (!facetMetaData[key]) return null;
    return facetMetaData[key];
  };
});

// Atom for getting all available facet_meta keys
export const facetMetaKeysAtom = atom((get) => {
  const facetMetaData = get(facetMetaDataAtom);
  if (!facetMetaData) return [];
  
  return Object.keys(facetMetaData);
});

// Atom for getting SEO slug by ID and facet type
export const getSeoSlugByIdAtom = atom((get) => {
  const mappings = get(facetMappingsAtom);
  if (!mappings) return null;
  
  return (id, facetType) => {
    if (!mappings[facetType]) return null;
    return mappings[facetType][id] || null;
  };
});

// Atom for getting SEO slug by label and facet type
export const getSeoSlugByLabelAtom = atom((get) => {
  const mappings = get(facetMappingsAtom);
  if (!mappings) return null;
  
  return (label, facetType) => {
    const labelKey = `${facetType}_label`;
    if (!mappings[labelKey]) return null;
    return mappings[labelKey][label] || null;
  };
});

// Atom for getting ID by SEO slug and facet type
export const getIdBySeoSlugAtom = atom((get) => {
  const mappings = get(facetMappingsAtom);
  if (!mappings) return null;
  
  return (slug, facetType) => {
    const reverseKey = `${facetType}_reverse`;
    if (!mappings[reverseKey]) return null;
    return mappings[reverseKey][slug] || null;
  };
});

// Atom for getting label by SEO slug and facet type
export const getLabelBySeoSlugAtom = atom((get) => {
  const mappings = get(facetMappingsAtom);
  if (!mappings) return null;
  
  return (slug, facetType) => {
    const reverseKey = `${facetType}_label_reverse`;
    if (!mappings[reverseKey]) return null;
    return mappings[reverseKey][slug] || null;
  };
});

// Atom for checking mappings readiness
export const mappingsReadyAtom = atom((get) => {
  const mappings = get(facetMappingsAtom);
  const loading = get(facetMetaLoadingStatusAtom);
  return mappings !== null && loading === 'idle';
});
